import React, { Suspense, useContext } from 'react';

import { ErrorBoundary } from 'react-error-boundary';
import { graphql, ReactRelayContext, useLazyLoadQuery } from 'react-relay';

import type { SizeType } from '@atlaskit/avatar/types';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { AvatarRelay } from '@atlassian/avatar-relay/avatar-relay';
import { normalizeAccountIdFromIdentityAri } from '@atlassian/conversation-assistant-utils/default-agent';
import { ResolvedAgentAvatar } from '@atlassian/editor-ai-common/ResolvedAgentAvatar';

import type { TelepointerAgentAvatarQuery } from './__generated__/TelepointerAgentAvatarQuery.graphql';

type Props = {
	agentIdentityAccountId?: string | null;
	agentName?: string;
	fallback: React.JSX.Element;
	size: SizeType;
};

const FetchedTelepointerAgentAvatar = ({
	accountId,
	agentName,
	fallback,
	size,
}: Omit<Props, 'agentIdentityAccountId'> & { accountId: string }): React.JSX.Element => {
	const data = useLazyLoadQuery<TelepointerAgentAvatarQuery>(
		graphql`
			query TelepointerAgentAvatarQuery($accountId: ID!, $shouldFetchAvatar: Boolean!) {
				user(accountId: $accountId) @include(if: $shouldFetchAvatar) {
					name
					picture
					...avatarRelay_user
				}
			}
		`,
		{
			accountId,
			shouldFetchAvatar: true,
		},
		{ fetchPolicy: 'store-or-network' },
	);
	if (!data.user?.picture?.trim()) {
		return fallback;
	}
	const name = data.user.name?.trim() || agentName;
	return (
		<AvatarRelay
			userFragment={data.user}
			name={name}
			label={name}
			size={size}
			borderColor="transparent"
		/>
	);
};

/** Editor owns fetching; AvatarRelay only renders the prefetched identity fragment. */
export const TelepointerAgentAvatar = ({
	agentIdentityAccountId,
	agentName,
	fallback,
	size,
}: Props): React.JSX.Element => {
	const relayContext = useContext(ReactRelayContext);
	if (!isExperimentEnabled('platform_editor_agent_avatar_prefetch')) {
		return (
			<ResolvedAgentAvatar
				agentIdentityAccountId={agentIdentityAccountId}
				agentName={agentName}
				fallback={fallback}
				size={size}
			/>
		);
	}
	const accountId = normalizeAccountIdFromIdentityAri(agentIdentityAccountId);
	if (!relayContext || !accountId) {
		return fallback;
	}
	return (
		<ErrorBoundary key={accountId} fallback={fallback}>
			<Suspense fallback={fallback}>
				<FetchedTelepointerAgentAvatar
					accountId={accountId}
					agentName={agentName}
					fallback={fallback}
					size={size}
				/>
			</Suspense>
		</ErrorBoundary>
	);
};
